import { computed, defineComponent, reactive, ref } from 'vue';
import { useSweetAlert } from '@/composables/sweetalert';
import { useStore } from 'vuex';
import { Holiday } from '@/store/holiday/holiday.model';
import { HolidaySchema } from '@/store/holiday/holiday.validator';
import { HolidayAction, HolidayGetter } from '@/store/holiday/holiday.module';
import Datepicker from '@vuepic/vue-datepicker';
import BaseModalComponent from '@/components/baseModal/BaseModal.vue';

export default defineComponent({
  name: 'HolidayFormComponent',
  components: {
    Datepicker,
    BaseModalComponent,
  },
  props: {
    holidayId: {
      type: Number,
    }
  },
  setup: (props) => {
    const swal = useSweetAlert();
    const store = useStore();
    const isEditMode = computed(() => props.holidayId != null);
    const title = computed(() => isEditMode.value ? 'แก้ไขวันหยุด' : 'เพิ่มวันหยุด');
    const holiday = computed<Holiday | undefined>(() => store.getters[HolidayGetter.BY_ID](props.holidayId));
    const holidayForm = ref<InstanceType<typeof BaseModalComponent>>();

    // form
    interface Form {
      id?: number | null;
      name?: string | null;
      date?: Date | null;
    }
    const formData = reactive<Form>({
      id: null,
      name: '',
      date: null,
    });
    const validator = computed(() => HolidaySchema.validate(formData, { abortEarly: false }));
    const errors = reactive({
      name: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'name')),
      date: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'date')),
    });
    const isValid = computed(() => !errors.name && !errors.date);
    const isLoading = computed<boolean>(() => store.getters[HolidayGetter.STATUS] === 'init' || store.getters[HolidayGetter.STATUS] === 'loading');
    const submited = ref(false);

    // show modal
    const show = () => {
      new Promise<void>((resolve) => {
        if (isEditMode.value) {
          formData.id = props.holidayId;
          formData.name = holiday.value?.name;
          formData.date = holiday.value?.date;
        }
        resolve();
      })
      .then(() => holidayForm.value?.show());
    };

    // hide modal
    const hide = () => {
      holidayForm.value?.hide();
    };

    // clear submit status
    const unsubmit = () => {
      submited.value = false;
    };

    // clear form
    const clearForm = () => {
      new Promise<void>((resolve) => {
        formData.id = null;
        formData.name = '';
        formData.date = null;
        resolve();
      }).then(() => unsubmit());
    };

    // submit form
    const submitClick = () => {
      submited.value = true;

      if (isValid.value) {
        if (isEditMode.value) {
          store.dispatch(HolidayAction.UPDATE, formData)
            .then(() => hide());
        } else {
          store.dispatch(HolidayAction.CREATE, formData)
            .then(() => hide());
        }
      }
    };

    // delete item
    const deleteClick = () => {
      swal.confirm('ลบข้อมูล!', 'คุณต้องการลบข้อมูลใช่หรือไม่?', 'ใช่ ลบเดี๋ยวนี้!', '#dc3545')
        .then((result) => {
          if (result.isConfirmed) {
            store.dispatch(HolidayAction.DELETE, formData)
              .then(() => hide());
          }
        });
    };

    return {
      title,
      formData,
      isEditMode,
      errors,
      submited,
      isLoading,
      holidayForm,
      show,
      hide,
      submitClick,
      deleteClick,
      unsubmit,
      clearForm,
    };
  }
});
